import { Filter, FilterConfig, FilterTypes, IsFilterable } from "@/lib/filterable";
import { IEntity } from "@/lib/utility/data/entity.interface";
import { formatYearsMonthDay } from "@/lib/utility/date-helper";
import repairEstimateService from "@/services/mrfiktiv/services/repair-estimate.service";
import {
  MrfiktivRepairEstimateViewModelGen,
  MrfiktivUpdateRepairEstimateDtoGen
} from "@/services/mrfiktiv/v1/data-contracts";
import { PartnerUserAccessLayer } from "@/store/modules/access-layers/partner-user.access-layer";
import { RepairEstimateDataAccessLayer } from "@/store/modules/access-layers/repair-estimate.access-layer";
import { SignDocumentDataAccessLayer } from "@/store/modules/access-layers/sign-document.access-layer";
import { IInvoiceLine, InvoiceLine } from "./invoice-line.entity";
import { ITimestamp, Timestamp } from "./timestamp.entity";
import { IWorkshopContractRate, WorkshopContractRate } from "./workshop-contract-rate.entity";
import VueRouter from "vue-router";
import { RepairEstimateGoToHelper } from "@/lib/utility/repair-estimate.go-to-helper";
import { RepairEstimateStatusEnum } from "@/lib/enum/repair-estimate-status.enum";
import { Reference, IReference } from "./reference.entity";
import { IInvoiceParty, InvoiceParty } from "./invoice-party.entity";
import { $t } from "@/lib/utility/t";

@IsFilterable
class RepairEstimateBase implements IEntity<IRepairEstimate, MrfiktivUpdateRepairEstimateDtoGen> {
  /** Id of the repairEstimate object */
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.repairEstimate.id"
  })
  id: string;

  /** The partnerId of the repairEstimate */
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.repairEstimate.partnerId"
  })
  partnerId: string;

  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.repairEstimate.userId",
    config: {
      itemCallback: () => PartnerUserAccessLayer.entities,
      mapItemToComponent: item => ({ item }),
      itemValue: "id",
      component: "refs-user"
    }
  })
  userId: string;

  /**
   * total
   */
  @FilterConfig({
    type: FilterTypes.NUMBER,
    displayName: "objects.repairEstimate.total"
  })
  total: number;

  /** timestamp */
  @FilterConfig({
    type: Timestamp
  })
  timestamp: ITimestamp;

  get totalReadable() {
    return this.total.toLocaleString("de-DE", { style: "currency", currency: "EUR" });
  }

  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.repairEstimate.number"
  })
  number: string;

  /** date of estimate */
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.repairEstimate.date"
  })
  date: string;

  get dateFormable(): string {
    if (!this.date) {
      return "";
    }
    return formatYearsMonthDay(new Date(this.date));
  }
  set dateFormable(date: string) {
    if (date) {
      this.date = new Date(date).toISOString();
    }

    this.date = date;
  }

  /** Document */
  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.contract.documentId",
    config: {
      itemCallback: () => SignDocumentDataAccessLayer.entities,
      mapItemToComponent: item => ({ item }),
      itemValue: "id",
      component: "refs-document"
    }
  })
  documentId: string;

  /** Document */
  @FilterConfig({
    type: WorkshopContractRate
  })
  workshopRate?: IWorkshopContractRate;

  /** Document */
  @FilterConfig({
    type: InvoiceLine
  })
  lineItems: IInvoiceLine[];

  @FilterConfig({
    type: Reference
  })
  refs: IReference[];

  @FilterConfig({
    type: FilterTypes.ENUM,
    displayName: "objects.repairEstimate.status",
    config: {
      items: Object.values(RepairEstimateStatusEnum).map(status => ({
        value: status,
        label: `enums.RepairEstimateStatusEnum.${status}`
      }))
    }
  })
  status: RepairEstimateStatusEnum;

  /** The buyer information */
  @FilterConfig({
    type: InvoiceParty
  })
  buyer?: IInvoiceParty;

  /** The seller information */
  @FilterConfig({
    type: InvoiceParty
  })
  seller?: IInvoiceParty;

  get statusColor() {
    switch (this.status) {
      case RepairEstimateStatusEnum.DRAFT:
        return "grey";
      case RepairEstimateStatusEnum.OPEN:
        return "info";
      default:
        return "warning";
    }
  }

  get statusReadable() {
    return $t(`enums.RequestStatusEnum.${this.status}`);
  }

  loading = false;

  constructor(data: Partial<MrfiktivRepairEstimateViewModelGen> | IRepairEstimate) {
    this.id = data.id ?? "";
    this.partnerId = data.partnerId ?? "";
    this.userId = data.userId ?? "";
    this.total = data.total ?? 0;
    this.number = data.number ?? "";
    this.timestamp = new Timestamp(data.timestamp);
    this.lineItems = (data.lineItems ?? []).map(line => new InvoiceLine(line));
    this.documentId = data.documentId ?? "";
    this.date = data.date ?? "";
    this.status = (data.status ?? RepairEstimateStatusEnum.DRAFT) as RepairEstimateStatusEnum;
    this.refs = Reference.filterDuplicates((data?.refs ?? []).map(ref => new Reference(ref)));
    this.buyer = data.buyer ? new InvoiceParty(data.buyer) : undefined;
    this.seller = data.seller ? new InvoiceParty(data.seller) : undefined;
  }

  map(data: MrfiktivRepairEstimateViewModelGen): this {
    this.id = data.id ?? "";
    this.partnerId = data.partnerId ?? "";
    this.userId = data.userId ?? "";
    this.total = data.total ?? 0;
    this.number = data.number ?? "";
    this.timestamp = new Timestamp(data.timestamp);
    this.lineItems = (data.lineItems ?? []).map(line => new InvoiceLine(line));
    this.documentId = data.documentId ?? "";
    this.date = data.date ?? "";
    this.status = (data.status ?? RepairEstimateStatusEnum.DRAFT) as RepairEstimateStatusEnum;
    this.refs = Reference.filterDuplicates((data?.refs ?? []).map(ref => new Reference(ref)));
    this.buyer = data.buyer ? new InvoiceParty(data.buyer) : undefined;
    this.seller = data.seller ? new InvoiceParty(data.seller) : undefined;

    return this;
  }

  async create(): Promise<this> {
    this.loading = true;
    try {
      const res = await repairEstimateService.create(this.partnerId, {
        date: this.date,
        documentId: this.documentId,
        lineItems: this.lineItems,
        number: this.number,
        total: this.total,
        workshopRate: this.workshopRate,
        refs: Reference.filterDuplicates(this.refs),
        buyer: this.buyer,
        seller: this.seller
      });

      this.map(res);
      RepairEstimateDataAccessLayer.set(this);
    } catch (e) {
      this.loading = false;
    }
    this.loading = false;

    return this;
  }

  async fetch(): Promise<this> {
    this.loading = true;
    try {
      const res = await repairEstimateService.getOne(this.partnerId, this.id);
      this.map(res);
      RepairEstimateDataAccessLayer.set(this);
    } catch (e) {
      this.loading = false;
      RepairEstimateDataAccessLayer.delete(this);
      throw e;
    }
    this.loading = false;

    return this;
  }

  async updatePartial(dto: Partial<MrfiktivUpdateRepairEstimateDtoGen>): Promise<this> {
    const res = await repairEstimateService.update(this.partnerId, this.id, dto);

    this.map(res);
    RepairEstimateDataAccessLayer.set(this);

    return this;
  }

  async update(): Promise<this> {
    this.loading = true;
    try {
      await this.updatePartial({
        date: this.date,
        documentId: this.documentId,
        lineItems: this.lineItems,
        number: this.number,
        total: this.total,
        workshopRate: this.workshopRate,
        refs: Reference.filterDuplicates(this.refs),
        status: this.status,
        buyer: this.buyer,
        seller: this.seller
      });
    } catch (e) {
      this.loading = false;
      throw e;
    }
    this.loading = false;

    return this;
  }

  async delete(): Promise<void> {
    this.loading = true;
    try {
      const res = await repairEstimateService.delete(this.partnerId, this.id);

      this.map(res);
      RepairEstimateDataAccessLayer.delete(this);
    } catch (e) {
      this.loading = false;
      throw e;
    }
    this.loading = false;
  }

  goTo(router: VueRouter) {
    return {
      table: () => new RepairEstimateGoToHelper(router).goToRepairEstimateTable({ partnerId: this.partnerId }),
      detail: () =>
        new RepairEstimateGoToHelper(router).goToRepairEstimateDetail({
          partnerId: this.partnerId,
          repairEstimateId: this.id
        })
    };
  }
}

type IRepairEstimate = RepairEstimateBase;
const RepairEstimate = Filter.createForClass(RepairEstimateBase);

export { IRepairEstimate, RepairEstimate };
